import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'
import KPICard from '../components/KPICard'
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, ReferenceLine, Cell
} from 'recharts'

// --- Formatting helpers ---

function parseDate(isoDate) {
    if (!isoDate) return null
    // Handle both "2026-04-01" and "2026-04-01T00:00:00.000Z"
    const str = typeof isoDate === 'string' ? isoDate.slice(0, 10) : new Date(isoDate).toISOString().slice(0, 10)
    return new Date(str + 'T00:00:00')
}

function formatMonth(isoDate) {
    const d = parseDate(isoDate)
    if (!d || isNaN(d)) return ''
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

function formatMonthShort(isoDate) {
    const d = parseDate(isoDate)
    if (!d || isNaN(d)) return ''
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
}

function formatDollars(val) {
    if (val == null) return '$0'
    const abs = Math.abs(val)
    const sign = val < 0 ? '-' : ''
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`
    return `${sign}$${Math.round(abs).toLocaleString()}`
}

function formatBBL(val) {
    if (val == null) return '0'
    if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(2)}M`
    if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`
    return val.toLocaleString()
}

// --- Type badge config ---

const TYPE_BADGE = {
    SWAP:               { label: 'SWAP',              bg: 'bg-blue-900',   text: 'text-blue-200',   bgLight: 'bg-blue-100',   textLight: 'text-blue-800' },
    PUT:                { label: 'PUT',               bg: 'bg-green-900',  text: 'text-green-200',  bgLight: 'bg-green-100',  textLight: 'text-green-800' },
    COLLAR:             { label: 'COLLAR',            bg: 'bg-purple-900', text: 'text-purple-200', bgLight: 'bg-purple-100', textLight: 'text-purple-800' },
    THREE_WAY_COLLAR:   { label: '3-WAY',             bg: 'bg-orange-900', text: 'text-orange-200', bgLight: 'bg-orange-100', textLight: 'text-orange-800' },
}

function TypeBadge({ type, isDark }) {
    const cfg = TYPE_BADGE[type] || { label: type, bg: 'bg-slate-700', text: 'text-gray-200', bgLight: 'bg-gray-200', textLight: 'text-gray-800' }
    return (
        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${isDark ? `${cfg.bg} ${cfg.text}` : `${cfg.bgLight} ${cfg.textLight}`}`}>
            {cfg.label}
        </span>
    )
}

// --- Custom tooltip for Forward Curve ---

function ForwardCurveTooltip({ active, payload, label, isDark }) {
    if (!active || !payload?.length) return null
    const bg = isDark ? '#1e293b' : '#ffffff'
    const border = isDark ? '#334155' : '#e2e8f0'
    const headColor = isDark ? '#fff' : '#111'
    return (
        <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
            <p style={{ fontWeight: 600, marginBottom: 4, color: headColor }}>{label}</p>
            {payload.map((entry, i) => (
                <p key={i} style={{ color: entry.color, margin: '2px 0' }}>
                    {entry.name}: ${entry.value?.toFixed(2)}/BBL
                    {entry.payload?.change != null && entry.name === 'Strip' && (
                        <span style={{ marginLeft: 8, color: entry.payload.change >= 0 ? '#22c55e' : '#ef4444', fontSize: 11 }}>
                            ({entry.payload.change >= 0 ? '+' : ''}{entry.payload.change?.toFixed(2)} / {entry.payload.changePct?.toFixed(1)}%)
                        </span>
                    )}
                </p>
            ))}
        </div>
    )
}

// --- Custom tooltip for Coverage Chart ---

function CoverageTooltip({ active, payload, label, isDark }) {
    if (!active || !payload?.length) return null
    const bg = isDark ? '#1e293b' : '#ffffff'
    const border = isDark ? '#334155' : '#e2e8f0'
    const headColor = isDark ? '#fff' : '#111'
    const hedged = payload.find(p => p.dataKey === 'hedgedBBL')
    const unhedged = payload.find(p => p.dataKey === 'unhedgedBBL')
    const entry = payload[0]?.payload
    return (
        <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
            <p style={{ fontWeight: 600, marginBottom: 4, color: headColor }}>{label}</p>
            {hedged && <p style={{ color: '#3b82f6', margin: '2px 0' }}>Hedged: {formatBBL(hedged.value)} BBL</p>}
            {unhedged && <p style={{ color: isDark ? '#475569' : '#94a3b8', margin: '2px 0' }}>Unhedged: {formatBBL(unhedged.value)} BBL</p>}
            {entry?.coverage != null && (
                <p style={{ color: isDark ? '#fbbf24' : '#d97706', margin: '2px 0', fontWeight: 600 }}>
                    Coverage: {entry.coverage.toFixed(1)}%
                </p>
            )}
            {entry?.stripPrice != null && (
                <p style={{ color: isDark ? '#94a3b8' : '#64748b', margin: '2px 0' }}>
                    Strip: ${entry.stripPrice.toFixed(2)}/BBL
                </p>
            )}
        </div>
    )
}

// --- Coverage % label rendered above bar stack ---

function CoverageLabel(props) {
    const { x, y, width, payload } = props
    const coverage = payload?.coverage
    if (coverage == null || coverage <= 0) return null
    return (
        <text x={x + width / 2} y={y - 4} fill="#fbbf24" fontSize={10} fontWeight={600} textAnchor="middle">
            {coverage.toFixed(1)}%
        </text>
    )
}

// --- Main component ---

function HedgeOverview() {
    const { theme } = useTheme()
    const navigate = useNavigate()
    const isDark = theme === 'dark'

    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [lastFetched, setLastFetched] = useState(null)
    const [deactivating, setDeactivating] = useState(null) // position id being deactivated
    const [refreshingStrip, setRefreshingStrip] = useState(false)
    const [stripRefreshMsg, setStripRefreshMsg] = useState(null)

    const handleRefreshStrip = useCallback(async () => {
        setRefreshingStrip(true)
        setStripRefreshMsg(null)
        try {
            const res = await fetch(`${apiConfig.baseUrl}/hedge/strip/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            })
            if (!res.ok) throw new Error(`API error ${res.status}`)
            const json = await res.json()
            setStripRefreshMsg(`${json.upserted} contracts updated`)
            // Reload dashboard data with fresh strip
            await fetchData()
            // Auto-clear message after 8 seconds
            setTimeout(() => setStripRefreshMsg(null), 8000)
        } catch (err) {
            setStripRefreshMsg(`Error: ${err.message}`)
            setTimeout(() => setStripRefreshMsg(null), 8000)
        } finally {
            setRefreshingStrip(false)
        }
    }, [])

    const fetchData = useCallback(async () => {
        setLoading(true)
        setError(null)
        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                const res = await fetch(`${apiConfig.baseUrl}/hedge`)
                if (!res.ok) throw new Error(`API error ${res.status}: ${res.statusText}`)
                const json = await res.json()
                setData(json)
                setLastFetched(new Date())
                setLoading(false)
                return
            } catch (err) {
                if (attempt === 1) {
                    setError(err.message)
                    setLoading(false)
                    return
                }
                await new Promise(r => setTimeout(r, 3000))
            }
        }
    }, [])

    // Auto-refresh every 30 minutes during business hours (7 AM – 7 PM)
    useEffect(() => {
        fetchData()
        const THIRTY_MIN = 30 * 60 * 1000
        const interval = setInterval(() => {
            const hour = new Date().getHours()
            if (hour >= 7 && hour < 19) fetchData()
        }, THIRTY_MIN)
        return () => clearInterval(interval)
    }, [fetchData])

    const handleDeactivate = async (positionId) => {
        if (!window.confirm('Deactivate this position? This cannot be undone.')) return
        setDeactivating(positionId)
        try {
            const res = await fetch(`${apiConfig.baseUrl}/hedge/positions?id=${positionId}`, {
                method: 'DELETE',
            })
            if (!res.ok) throw new Error('Failed to deactivate position')
            await fetchData()
        } catch (err) {
            alert(`Error: ${err.message}`)
        } finally {
            setDeactivating(null)
        }
    }

    // --- Loading state ---
    if (loading && !data) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviator-600"></div>
            </div>
        )
    }

    // --- Error state ---
    if (error && !data) {
        return (
            <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">
                <p className="font-semibold">Error loading hedge data</p>
                <p className="text-sm mt-1">{error}</p>
                <button onClick={fetchData}
                    className="mt-3 text-sm bg-red-800 hover:bg-red-700 text-white px-3 py-1 rounded transition">
                    Retry
                </button>
            </div>
        )
    }

    // --- Data extraction ---
    const summary = data?.summary || {}
    const positions = data?.positions || []
    const strip = data?.strip || []
    const coverageByMonth = data?.coverageByMonth || []

    // Chart colors
    const chartColors = {
        strip:     '#3b82f6',
        floor:     '#22c55e',
        ceiling:   '#ef4444',
        hedged:    '#3b82f6',
        unhedged:  isDark ? '#334155' : '#e2e8f0',
        grid:      isDark ? '#334155' : '#e2e8f0',
        text:      isDark ? '#94a3b8' : '#64748b',
        tooltipBg: isDark ? '#1e293b' : '#ffffff',
    }

    // Build forward curve dataset — merge strip prices with floor/ceiling reference lines
    const forwardCurveData = strip.map(s => ({
        month: formatMonthShort(s.month),
        settle: s.settle,
        change: s.change,
        changePct: s.changePct,
        floor: summary.weightedFloor,
        ceiling: summary.weightedCeiling,
    }))

    // Build coverage chart dataset
    const coverageChartData = coverageByMonth.map(m => ({
        month: formatMonthShort(m.month),
        hedgedBBL: m.hedgedBBL || 0,
        unhedgedBBL: Math.max(0, (m.netBBL || 0) - (m.hedgedBBL || 0)),
        coverage: m.coverage,
        stripPrice: m.stripPrice,
    }))

    // MTM P&L formatting — green if positive, red if negative
    const mtm = summary.mtmPnL || 0
    const mtmColor = mtm >= 0
        ? (isDark ? 'text-green-400' : 'text-green-600')
        : (isDark ? 'text-red-400' : 'text-red-600')

    const navBtnClass = `px-3 py-1.5 text-sm rounded-lg transition ${
        isDark
            ? 'bg-slate-900 text-gray-300 hover:bg-slate-800 border border-slate-800'
            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
    }`

    return (
        <div className="space-y-6">

            {/* ── Page Header ── */}
            <div className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 border-b-2 ${isDark ? 'border-blue-800' : 'border-blue-200'} pb-4`}>
                <div>
                    <h1 className={`text-xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                        <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                        Hedge Overview
                    </h1>
                    <p className={`${isDark ? 'text-blue-300' : 'text-blue-700'} text-xs sm:text-sm mt-1 ml-3 sm:ml-4`}>
                        {summary.positionCount || 0} active {summary.positionCount === 1 ? 'position' : 'positions'}
                        {summary.totalHedgedBBL > 0 && (
                            <span> &bull; {formatBBL(summary.totalHedgedBBL)} BBL hedged</span>
                        )}
                        {summary.stripFetchDate && (
                            <span className={`ml-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                &bull; Strip as of {formatMonth(summary.stripFetchDate)}
                            </span>
                        )}
                        {lastFetched && (
                            <span className={`ml-2 text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                                &bull; Updated {lastFetched.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            </span>
                        )}
                        {loading && <span className="ml-2 text-xs opacity-60">Refreshing...</span>}
                    </p>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => navigate('/hedge/scenarios')} className={navBtnClass}>
                        Scenarios
                    </button>
                    <button onClick={() => navigate('/hedge/positions')} className={navBtnClass}>
                        Positions
                    </button>
                    <button onClick={() => navigate('/hedge/options')} className={navBtnClass}>
                        Options
                    </button>
                    <button
                        onClick={handleRefreshStrip}
                        disabled={refreshingStrip}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition disabled:opacity-50 ${
                            isDark
                                ? 'bg-green-900/60 text-green-300 hover:bg-green-900 border border-green-800'
                                : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-300'
                        }`}
                        title="Fetch latest WTI futures strip from Yahoo Finance"
                        aria-label="Refresh WTI strip from Yahoo Finance"
                    >
                        <svg className={`w-4 h-4 ${refreshingStrip ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {refreshingStrip ? 'Refreshing Strip...' : 'Refresh Strip'}
                    </button>
                    {stripRefreshMsg && (
                        <span className={`text-xs px-2 py-1 rounded ${
                            stripRefreshMsg.startsWith('Error')
                                ? (isDark ? 'text-red-400 bg-red-900/30' : 'text-red-600 bg-red-50')
                                : (isDark ? 'text-green-400 bg-green-900/30' : 'text-green-600 bg-green-50')
                        }`}>
                            {stripRefreshMsg}
                        </span>
                    )}
                    <button onClick={fetchData}
                        className={`px-2.5 py-1.5 text-sm rounded-lg transition ${
                            isDark
                                ? 'bg-slate-900 text-gray-300 hover:bg-slate-800 border border-slate-800'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`} title="Refresh data" aria-label="Refresh hedge data">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* ── 1. KPI Cards ── */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
                <KPICard
                    title="Weighted Floor"
                    value={summary.weightedFloor != null ? `$${summary.weightedFloor.toFixed(2)}` : '$--'}
                    unit="/BBL"
                    subtitle="Downside protection"
                />
                <KPICard
                    title="Weighted Ceiling"
                    value={summary.weightedCeiling != null ? `$${summary.weightedCeiling.toFixed(2)}` : '$--'}
                    unit="/BBL"
                    subtitle="Upside cap"
                />
                <KPICard
                    title="Avg Strip (12mo)"
                    value={summary.avgStrip12Mo != null ? `$${summary.avgStrip12Mo.toFixed(2)}` : '$--'}
                    unit="/BBL"
                    subtitle="Forward curve avg"
                />
                <div className={`kpi-card !p-4 sm:!p-6`}>
                    <p className="kpi-card-title !text-[10px] sm:!text-sm">MTM P&amp;L</p>
                    <div className={`kpi-card-value !text-xl sm:!text-3xl ${mtmColor}`}>
                        {formatDollars(mtm)}
                    </div>
                    <p className="kpi-card-subtitle !text-[10px] sm:!text-sm">Mark-to-market</p>
                </div>
                <KPICard
                    title="Coverage"
                    value={summary.overallCoverage != null ? `${summary.overallCoverage.toFixed(1)}%` : '--%'}
                    subtitle={`${formatBBL(summary.totalHedgedBBL)} / ${formatBBL(summary.totalNetBBL)} BBL`}
                />
            </div>

            {/* ── 2. Forward Curve Chart ── */}
            <div className="chart-container">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
                    <div>
                        <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            WTI Forward Curve
                        </h2>
                        <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Strip vs weighted floor &amp; ceiling
                        </p>
                    </div>
                    {strip.length > 0 && (
                        <div className={`flex items-center gap-4 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            <span>
                                Front month: <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    ${strip[0]?.settle?.toFixed(2)}
                                </span>
                            </span>
                            {strip[0]?.change != null && (
                                <span className={strip[0].change >= 0 ? (isDark ? 'text-green-400' : 'text-green-600') : (isDark ? 'text-red-400' : 'text-red-600')}>
                                    {strip[0].change >= 0 ? '+' : ''}{strip[0].change.toFixed(2)} ({strip[0].changePct?.toFixed(1)}%)
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {forwardCurveData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={forwardCurveData} margin={{ top: 10, right: 16, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                            <XAxis
                                dataKey="month"
                                tick={{ fill: chartColors.text, fontSize: 11 }}
                                interval="preserveStartEnd"
                            />
                            <YAxis
                                tickFormatter={v => `$${v}`}
                                tick={{ fill: chartColors.text, fontSize: 12 }}
                                width={52}
                                domain={['auto', 'auto']}
                            />
                            <Tooltip
                                content={<ForwardCurveTooltip isDark={isDark} />}
                            />
                            <Legend wrapperStyle={{ fontSize: 13, color: chartColors.text }} />
                            <Line
                                type="monotone"
                                dataKey="settle"
                                name="Strip"
                                stroke={chartColors.strip}
                                strokeWidth={2.5}
                                dot={false}
                                activeDot={{ r: 4 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="floor"
                                name="Floor"
                                stroke={chartColors.floor}
                                strokeWidth={1.5}
                                strokeDasharray="6 3"
                                dot={false}
                            />
                            <Line
                                type="monotone"
                                dataKey="ceiling"
                                name="Ceiling"
                                stroke={chartColors.ceiling}
                                strokeWidth={1.5}
                                strokeDasharray="6 3"
                                dot={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <div className={`flex items-center justify-center h-48 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        No strip data available
                    </div>
                )}
            </div>

            {/* ── 3. Coverage by Month ── */}
            <div className="chart-container">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
                    <div>
                        <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Coverage by Month
                        </h2>
                        <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Hedged vs unhedged net production (BBL)
                        </p>
                    </div>
                </div>

                {coverageChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart
                            data={coverageChartData}
                            margin={{ top: 20, right: 16, left: 10, bottom: 5 }}
                            barCategoryGap="25%"
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                            <XAxis
                                dataKey="month"
                                tick={{ fill: chartColors.text, fontSize: 11 }}
                                interval="preserveStartEnd"
                            />
                            <YAxis
                                tickFormatter={v => formatBBL(v)}
                                tick={{ fill: chartColors.text, fontSize: 12 }}
                                width={58}
                            />
                            <Tooltip content={<CoverageTooltip isDark={isDark} />} />
                            <Legend wrapperStyle={{ fontSize: 13, color: chartColors.text }} />
                            <Bar dataKey="hedgedBBL" name="Hedged BBL" stackId="a" fill={chartColors.hedged} radius={[0, 0, 0, 0]} />
                            <Bar
                                dataKey="unhedgedBBL"
                                name="Unhedged BBL"
                                stackId="a"
                                fill={chartColors.unhedged}
                                radius={[3, 3, 0, 0]}
                                label={<CoverageLabel />}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div className={`flex items-center justify-center h-48 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        No coverage data available
                    </div>
                )}
            </div>

            {/* ── 4. Active Positions Table ── */}
            <div className="chart-container">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                    <div>
                        <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Active Positions
                            <span className={`ml-2 text-sm font-normal ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                ({positions.length})
                            </span>
                        </h2>
                    </div>
                    <button
                        onClick={() => navigate('/hedge/positions')}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition ${
                            isDark
                                ? 'bg-blue-700 hover:bg-blue-600 text-white'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}>
                        Manage Positions
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>

                {positions.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className={isDark ? 'text-gray-400 bg-slate-800/80' : 'text-gray-500 bg-gray-100'}>
                                    <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Month</th>
                                    <th className="text-left px-3 py-2 font-semibold">Type</th>
                                    <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Volume (BBL)</th>
                                    <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Strike / Price</th>
                                    <th className="text-right px-3 py-2 font-semibold">Premium</th>
                                    <th className="text-right px-3 py-2 font-semibold">MTM</th>
                                    <th className="text-left px-3 py-2 font-semibold">Counterparty</th>
                                    <th className="text-center px-3 py-2 font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {positions.map((pos, i) => {
                                    // Compute MTM from SQL columns
                                    const stripPrice = pos.StripPrice || 0
                                    const vol = pos.VolumeBBL || 0
                                    const premium = pos.PremiumPaid || 0
                                    let mtmPos = 0
                                    if (pos.StructureType === 'SWAP' && pos.SwapPrice) {
                                        mtmPos = (pos.SwapPrice - stripPrice) * vol
                                    } else if (pos.StructureType === 'PUT' && pos.PutStrike) {
                                        mtmPos = (Math.max(0, pos.PutStrike - stripPrice) - premium) * vol
                                    } else if ((pos.StructureType === 'COLLAR' || pos.StructureType === 'THREE_WAY_COLLAR') && pos.PutStrike && pos.CallStrike) {
                                        const putPay = Math.max(0, pos.PutStrike - stripPrice)
                                        const callObl = Math.max(0, stripPrice - pos.CallStrike)
                                        mtmPos = (putPay - callObl - premium) * vol
                                    }
                                    const mtmPosColor = mtmPos >= 0
                                        ? (isDark ? 'text-green-400' : 'text-green-600')
                                        : (isDark ? 'text-red-400' : 'text-red-600')
                                    const rowBorder = isDark
                                        ? 'border-t border-slate-700/60'
                                        : 'border-t border-gray-200'
                                    const rowHover = isDark
                                        ? 'hover:bg-slate-700/30'
                                        : 'hover:bg-gray-50'
                                    // Build strike display from SQL columns
                                    let strikeDisplay = '--'
                                    if (pos.StructureType === 'SWAP' && pos.SwapPrice != null) {
                                        strikeDisplay = `$${pos.SwapPrice.toFixed(2)}`
                                    } else if (pos.StructureType === 'PUT' && pos.PutStrike != null) {
                                        strikeDisplay = `$${pos.PutStrike.toFixed(2)}`
                                    } else if ((pos.StructureType === 'COLLAR' || pos.StructureType === 'THREE_WAY_COLLAR') && pos.PutStrike != null && pos.CallStrike != null) {
                                        strikeDisplay = `$${pos.PutStrike.toFixed(2)} / $${pos.CallStrike.toFixed(2)}`
                                    }
                                    return (
                                        <tr key={pos.PositionId || i} className={`${rowBorder} ${rowHover} ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                            <td className="px-3 py-2 whitespace-nowrap font-medium">
                                                {formatMonth(pos.ContractMonth)}
                                            </td>
                                            <td className="px-3 py-2">
                                                <TypeBadge type={pos.StructureType} isDark={isDark} />
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono">
                                                {pos.VolumeBBL != null ? pos.VolumeBBL.toLocaleString() : '--'}
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono">
                                                {strikeDisplay}
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono">
                                                {pos.PremiumPaid != null ? formatDollars(pos.PremiumPaid) : '--'}
                                            </td>
                                            <td className={`px-3 py-2 text-right font-mono font-semibold ${mtmPosColor}`}>
                                                {formatDollars(mtmPos)}
                                            </td>
                                            <td className={`px-3 py-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                {pos.Counterparty || '--'}
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => navigate('/hedge/positions')}
                                                        className={`text-xs px-2 py-1 rounded transition ${
                                                            isDark
                                                                ? 'bg-blue-900/60 text-blue-300 hover:bg-blue-900'
                                                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                                        }`}
                                                        aria-label={`Edit position for ${formatMonth(pos.ContractMonth)}`}>
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeactivate(pos.PositionId)}
                                                        disabled={deactivating === pos.PositionId}
                                                        className={`text-xs px-2 py-1 rounded transition disabled:opacity-50 ${
                                                            isDark
                                                                ? 'bg-red-900/40 text-red-300 hover:bg-red-900/70'
                                                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                                                        }`}
                                                        aria-label={`Deactivate position for ${formatMonth(pos.ContractMonth)}`}>
                                                        {deactivating === pos.PositionId ? '...' : 'Deactivate'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={`flex flex-col items-center justify-center py-12 gap-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        <p className="text-sm">No active hedge positions</p>
                        <button
                            onClick={() => navigate('/hedge/positions')}
                            className={`text-sm px-4 py-2 rounded-lg transition ${
                                isDark
                                    ? 'bg-blue-700 hover:bg-blue-600 text-white'
                                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                            }`}>
                            Add Position
                        </button>
                    </div>
                )}
            </div>

            {/* ── 5. Navigation Links ── */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                    onClick={() => navigate('/hedge/scenarios')}
                    className={`flex items-center gap-3 p-4 rounded-xl text-left transition group ${
                        isDark
                            ? 'bg-slate-900 border border-slate-800 hover:border-blue-700 hover:bg-slate-800'
                            : 'bg-white border border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                    }`}
                    aria-label="Navigate to price scenarios">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                        isDark ? 'bg-blue-900/60' : 'bg-blue-100'
                    }`}>
                        <svg className={`w-5 h-5 ${isDark ? 'text-blue-300' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                    </div>
                    <div>
                        <p className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Price Scenarios
                        </p>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Stress-test hedge book
                        </p>
                    </div>
                    <svg className={`w-4 h-4 ml-auto flex-shrink-0 ${isDark ? 'text-gray-600 group-hover:text-blue-400' : 'text-gray-300 group-hover:text-blue-500'} transition`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>

                <button
                    onClick={() => navigate('/hedge/positions')}
                    className={`flex items-center gap-3 p-4 rounded-xl text-left transition group ${
                        isDark
                            ? 'bg-slate-900 border border-slate-800 hover:border-blue-700 hover:bg-slate-800'
                            : 'bg-white border border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                    }`}
                    aria-label="Navigate to manage positions">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                        isDark ? 'bg-blue-900/60' : 'bg-blue-100'
                    }`}>
                        <svg className={`w-5 h-5 ${isDark ? 'text-blue-300' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                    </div>
                    <div>
                        <p className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Manage Positions
                        </p>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Add, edit, or close hedges
                        </p>
                    </div>
                    <svg className={`w-4 h-4 ml-auto flex-shrink-0 ${isDark ? 'text-gray-600 group-hover:text-blue-400' : 'text-gray-300 group-hover:text-blue-500'} transition`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>

                <button
                    onClick={() => navigate('/hedge/options')}
                    className={`flex items-center gap-3 p-4 rounded-xl text-left transition group ${
                        isDark
                            ? 'bg-slate-900 border border-slate-800 hover:border-blue-700 hover:bg-slate-800'
                            : 'bg-white border border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                    }`}
                    aria-label="Navigate to options analysis">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                        isDark ? 'bg-blue-900/60' : 'bg-blue-100'
                    }`}>
                        <svg className={`w-5 h-5 ${isDark ? 'text-blue-300' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                    </div>
                    <div>
                        <p className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Options Analysis
                        </p>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Puts, collars, breakeven
                        </p>
                    </div>
                    <svg className={`w-4 h-4 ml-auto flex-shrink-0 ${isDark ? 'text-gray-600 group-hover:text-blue-400' : 'text-gray-300 group-hover:text-blue-500'} transition`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>

        </div>
    )
}

export default HedgeOverview
